// ============================================================================
// PLAYER ENTITY
// ============================================================================

import { 
  EntityId, 
  Vector2, 
  PlayerState, 
  PlayerInput, 
  WeaponState, 
  AbilityState,
  HeroId,
  Team,
  WeaponId
} from '@/shared/types';
import { HEROES, WEAPONS, GAMEPLAY } from '@/shared/constants';
import { Vec2, generateId } from '@/shared/utils';
import { CollisionBody, ColliderType, CollisionLayers } from '@/engine/CollisionSystem';

export class Player {
  // Identity
  public readonly id: EntityId;
  public name: string;
  public peerId: string;
  public team: Team;
  public heroId: HeroId;

  // Transform
  public position: Vector2;
  public velocity: Vector2;
  public rotation: number;

  // Previous state for interpolation
  public prevPosition: Vector2;
  public prevRotation: number;
  
  // Network interpolation target (for remote players on clients)
  public targetPosition: Vector2 | null = null;
  public targetRotation: number | null = null;

  // Stats
  public health: number;
  public maxHealth: number;
  public shield: number;
  public maxShield: number;
  public alive: boolean;
  public respawnTime: number;

  // Economy
  public credits: number;
  public kills: number;
  public deaths: number;
  public assists: number;

  // Grenades
  public grenadeCount: number;
  public lastGrenadeThrowTime: number;

  // Radio calls
  public bombCallCount: number; // Max 1
  public missileStrikeCount: number; // Max 1

  // Weapon inventory (slots 1-10)
  public weaponInventory: (WeaponState | null)[];
  public currentWeaponSlot: number;
  public weapon: WeaponState;  // Current active weapon

  // Abilities
  public abilities: AbilityState[];
  public ultimateCharge: number;

  // Current input
  public input: PlayerInput;

  // Combat state
  public lastDamageTime: number;
  public lastAttackerId: EntityId | null;
  public deathTime: number;  // Time when player died (for corpse fade)

  // Network state
  public isDisconnected: boolean;

  // Movement modifiers
  public speedMultiplier: number;
  public isSlowed: boolean;
  public isStunned: boolean;
  public isInvisible: boolean;

  // Passive modifiers (hero-specific bonuses)
  public damageReduction: number;    // % damage reduction (0-1)
  public damageBonus: number;        // % bonus damage dealt (0-1)
  public shieldRegenBonus: number;   // % faster shield regen (0-1)
  public fireRateBonus: number;      // % faster fire rate (0-1)
  public isFireImmune: boolean;      // Immune to fire damage
  public healingBonus: number;       // % bonus to healing received (0-1)

  constructor(
    peerId: string,
    name: string,
    team: Team,
    heroId: HeroId,
    spawnPosition: Vector2
  ) {
    this.id = generateId('player');
    this.peerId = peerId;
    this.name = name;
    this.team = team;
    this.heroId = heroId;

    const heroDef = HEROES[heroId];
    
    // Transform
    this.position = { ...spawnPosition };
    this.prevPosition = { ...spawnPosition };
    this.velocity = Vec2.zero();
    this.rotation = 0;
    this.prevRotation = 0;

    // Stats
    this.maxHealth = heroDef.health;
    this.health = this.maxHealth;
    this.maxShield = heroDef.shield;
    this.shield = this.maxShield;
    this.alive = true;
    this.respawnTime = 0;

    // Economy
    this.credits = GAMEPLAY.STARTING_CREDITS;
    this.kills = 0;
    this.deaths = 0;
    this.assists = 0;

    // Grenades
    this.grenadeCount = 0;
    this.lastGrenadeThrowTime = 0;

    // Radio calls
    this.bombCallCount = 0;
    this.missileStrikeCount = 0;

    // Weapon inventory - start with only burst pistol
    const defaultWeapon = WEAPONS[WeaponId.BurstPistol];
    const pistolState: WeaponState = {
      weaponId: WeaponId.BurstPistol,
      ammo: defaultWeapon.ammoCapacity,
      maxAmmo: defaultWeapon.ammoCapacity,
      reloading: false,
      reloadEndTime: 0,
      lastFireTime: 0,
    };
    
    // Initialize weapon inventory (10 slots) - start with pistol only
    // Other weapons are purchased through the buy menu
    this.weaponInventory = [
      pistolState,   // Slot 1: Burst Pistol (key 1) - always available
      null,          // Slot 2: Neo SMG-9 (key 2) - buy to unlock
      null,          // Slot 3: Pulse Rifle (key 3) - buy to unlock
      null,          // Slot 4: Scatter Shot (key 4) - buy to unlock
      null,          // Slot 5: Rail Sniper (key 5) - buy to unlock
      null,          // Slot 6: Shard Launcher (key 6) - buy to unlock
      null,          // Slot 7: Nano Repeater (key 7) - buy to unlock
      null,          // Slot 8: Arc Welder (key 8) - buy to unlock
      null,          // Slot 9: Phase Repeater (key 9) - buy to unlock
      null,          // Slot 10: Plasma Cannon (key 0) - buy to unlock
    ];
    this.currentWeaponSlot = 0;
    this.weapon = pistolState;

    // Abilities
    this.abilities = heroDef.abilities.map((_, index) => ({
      abilityIndex: index,
      cooldownEndTime: 0,
      charges: heroDef.abilities[index]?.charges ?? 1,
      active: false,
      duration: 0,
      ultimateCharge: 0,
    }));
    this.ultimateCharge = 0;

    // Input
    this.input = this.createEmptyInput();

    // Combat
    this.lastDamageTime = 0;
    this.lastAttackerId = null;
    this.deathTime = 0;

    // Network
    this.isDisconnected = false;

    // Modifiers
    this.speedMultiplier = heroDef.speed;
    this.isSlowed = false;
    this.isStunned = false;
    this.isInvisible = false;

    // Initialize passive modifiers
    this.damageReduction = 0;
    this.damageBonus = 0;
    this.shieldRegenBonus = 0;
    this.fireRateBonus = 0;
    this.isFireImmune = false;
    this.healingBonus = 0;

    // Apply hero-specific passive abilities
    this.applyHeroPassives();
  }

  /**
   * Apply hero-specific passive bonuses based on heroId
   */
  private applyHeroPassives(): void {
    switch (this.heroId) {
      case 'spectre':
        // Ghost Protocol: 10% damage reduction (stealthy = harder to pin down)
        this.damageReduction = 0.10;
        break;
      case 'byte':
        // Quick Recovery: 25% faster shield regeneration
        this.shieldRegenBonus = 0.25;
        break;
      case 'vanta':
        // Titan Armor: Already has +50 base shield, also 15% damage reduction
        this.damageReduction = 0.15;
        break;
      case 'katana':
        // Swift Strike: 15% speed boost (already in heroDef.speed = 1.15)
        // Also +10% damage (assassin hits hard)
        this.damageBonus = 0.10;
        break;
      case 'glyph':
        // Keen Eyes: 10% damage bonus (precision)
        this.damageBonus = 0.10;
        break;
      case 'spark':
        // Fireproof: Immune to fire damage + 10% fire rate
        this.isFireImmune = true;
        this.fireRateBonus = 0.10;
        break;
      case 'zero':
        // Steady Aim: 15% damage bonus (sniper precision)
        this.damageBonus = 0.15;
        break;
      case 'mira':
        // Combat Medic: 25% bonus healing received + given
        this.healingBonus = 0.25;
        break;
    }
  }

  private createEmptyInput(): PlayerInput {
    return {
      moveX: 0,
      moveY: 0,
      aimX: 0,
      aimY: 0,
      shoot: false,
      reload: false,
      interact: false,
      throwGrenade: false,
      useBombCall: false,
      useMissileStrike: false,
      ability1: false,
      ability2: false,
      ultimate: false,
      weaponSlot: 0,
      sequence: 0,
    };
  }

  update(dt: number, time: number): void {
    // Store previous state
    this.prevPosition = { ...this.position };
    this.prevRotation = this.rotation;

    if (!this.alive) return;
    if (this.isStunned) return;

    // Update rotation to face aim point
    const aimDir = Vec2.sub(
      { x: this.input.aimX, y: this.input.aimY },
      this.position
    );
    this.rotation = Vec2.angle(aimDir);

    // Update movement
    this.updateMovement(dt);

    // Update weapon
    this.updateWeapon(time);

    // Update abilities
    this.updateAbilities(time);

    // Regenerate shield when out of combat (apply shieldRegenBonus passive)
    if (time - this.lastDamageTime > 3000 && this.shield < this.maxShield) {
      const regenRate = 0.05 * (1 + this.shieldRegenBonus); // Base 5% per second + bonus
      this.shield = Math.min(this.maxShield, this.shield + this.maxShield * regenRate * (dt / 1000));
    }
  }

  private updateMovement(dt: number): void {
    let speed = GAMEPLAY.BASE_SPEED * this.speedMultiplier;
    
    if (this.isSlowed) {
      speed *= 0.6;
    }

    // Calculate target velocity
    const moveDir = Vec2.normalize({ x: this.input.moveX, y: this.input.moveY });
    const targetVelocity = Vec2.mul(moveDir, speed);

    // Apply acceleration/friction
    const acceleration = GAMEPLAY.ACCELERATION;
    const friction = GAMEPLAY.FRICTION;

    if (Vec2.length(targetVelocity) > 0) {
      // Accelerate towards target
      this.velocity = Vec2.lerp(this.velocity, targetVelocity, 1 - Math.exp(-acceleration * dt / 1000));
    } else {
      // Apply friction
      const frictionFactor = 1 - Math.exp(-friction * dt / 1000);
      this.velocity = Vec2.mul(this.velocity, 1 - frictionFactor);
    }

    // Apply velocity
    this.position = Vec2.add(this.position, Vec2.mul(this.velocity, dt / 1000));
  }

  private updateWeapon(time: number): void {
    // Check if reload is complete
    if (this.weapon.reloading && time >= this.weapon.reloadEndTime) {
      this.weapon.ammo = this.weapon.maxAmmo;
      this.weapon.reloading = false;
    }

    // Start reload if requested and not already reloading
    if (this.input.reload && !this.weapon.reloading && this.weapon.ammo < this.weapon.maxAmmo) {
      const weaponDef = WEAPONS[this.weapon.weaponId];
      this.weapon.reloading = true;
      this.weapon.reloadEndTime = time + weaponDef.reloadTime;
    }
  }

  private updateAbilities(time: number): void {
    // Update active ability durations
    for (const ability of this.abilities) {
      if (ability.active && ability.duration > 0) {
        if (time >= ability.duration) {
          ability.active = false;
          ability.duration = 0;
        }
      }

      // Restore charges
      const abilityDef = HEROES[this.heroId].abilities[ability.abilityIndex];
      if (abilityDef && ability.charges < abilityDef.charges) {
        if (time >= ability.cooldownEndTime) {
          ability.charges++;
          if (ability.charges < abilityDef.charges) {
            ability.cooldownEndTime = time + abilityDef.chargeTime;
          }
        }
      }
    }
  }

  getWeaponDefinition() {
    return WEAPONS[this.weapon.weaponId];
  }

  /**
   * Get effective fire rate including passive bonuses
   */
  getEffectiveFireRate(): number {
    const weaponDef = this.getWeaponDefinition();
    return weaponDef.fireRate * (1 + this.fireRateBonus);
  }

  /**
   * Get effective damage including passive bonuses
   */
  getEffectiveDamage(): number {
    const weaponDef = this.getWeaponDefinition();
    return weaponDef.damage * (1 + this.damageBonus);
  }

  canFire(time: number): boolean {
    if (!this.alive) return false;
    if (this.weapon.reloading) return false;
    if (this.weapon.ammo <= 0) return false;
    if (this.isStunned) return false;

    // Use effective fire rate (includes passive bonus)
    const fireInterval = 1000 / this.getEffectiveFireRate();
    
    return time >= this.weapon.lastFireTime + fireInterval;
  }

  fire(time: number): void {
    if (!this.canFire(time)) return;

    this.weapon.lastFireTime = time;
    this.weapon.ammo--;

    // Auto-reload when empty
    if (this.weapon.ammo <= 0) {
      const weaponDef = WEAPONS[this.weapon.weaponId];
      this.weapon.reloading = true;
      this.weapon.reloadEndTime = time + weaponDef.reloadTime;
    }
  }

  canUseAbility(abilityIndex: number, _time: number): boolean {
    if (!this.alive) return false;
    if (this.isStunned) return false;
    if (abilityIndex < 0 || abilityIndex >= this.abilities.length) return false;

    const ability = this.abilities[abilityIndex];
    if (!ability) return false;

    const abilityDef = HEROES[this.heroId].abilities[abilityIndex];
    if (!abilityDef) return false;

    // Check ultimate charge
    if (abilityDef.isUltimate && this.ultimateCharge < abilityDef.ultimateCost) {
      return false;
    }

    // Check charges
    return ability.charges > 0;
  }

  useAbility(abilityIndex: number, time: number): void {
    if (!this.canUseAbility(abilityIndex, time)) return;

    const ability = this.abilities[abilityIndex]!;
    const abilityDef = HEROES[this.heroId].abilities[abilityIndex]!;

    ability.charges--;
    ability.cooldownEndTime = time + abilityDef.cooldown;

    if (abilityDef.duration > 0) {
      ability.active = true;
      ability.duration = time + abilityDef.duration;
    }

    if (abilityDef.isUltimate) {
      this.ultimateCharge = 0;
    }
  }

  takeDamage(amount: number, attackerId: EntityId | null, time: number, damageType?: string): number {
    if (!this.alive) return 0;

    // Check fire immunity
    if (damageType === 'fire' && this.isFireImmune) {
      return 0; // No damage taken
    }

    // Apply passive damage reduction
    let actualDamage = amount * (1 - this.damageReduction);
    
    // Apply to shield first
    if (this.shield > 0) {
      const shieldDamage = Math.min(this.shield, actualDamage);
      this.shield -= shieldDamage;
      actualDamage -= shieldDamage;
    }

    // Apply to health
    this.health = Math.max(0, this.health - actualDamage);
    this.lastDamageTime = time;
    this.lastAttackerId = attackerId;

    if (this.health <= 0) {
      this.die(time);
    }

    return amount - actualDamage; // Return damage absorbed by shield
  }

  heal(amount: number): number {
    if (!this.alive) return 0;

    // Apply healing bonus passive
    const bonusHeal = amount * (1 + this.healingBonus);
    const actualHeal = Math.min(this.maxHealth - this.health, bonusHeal);
    this.health += actualHeal;
    return actualHeal;
  }

  addShield(amount: number): number {
    const actualShield = Math.min(this.maxShield - this.shield, amount);
    this.shield += actualShield;
    return actualShield;
  }

  die(time: number): void {
    this.alive = false;
    this.health = 0;
    this.deaths++;
    this.velocity = Vec2.zero();
    this.deathTime = time;  // Track when death occurred
    this.respawnTime = time + GAMEPLAY.ROUND_TIME; // No respawn in round-based
  }

  respawn(position: Vector2): void {
    this.alive = true;
    this.health = this.maxHealth;
    this.shield = this.maxShield;
    this.position = { ...position };
    this.prevPosition = { ...position };
    this.velocity = Vec2.zero();
    this.isSlowed = false;
    this.isStunned = false;
    this.isInvisible = false;
    
    // Reset weapon
    const weaponDef = WEAPONS[this.weapon.weaponId];
    this.weapon.ammo = weaponDef.ammoCapacity;
    this.weapon.reloading = false;
  }

  resetAbilities(): void {
    const heroDef = HEROES[this.heroId];
    for (let i = 0; i < this.abilities.length; i++) {
      const ability = this.abilities[i];
      const abilityDef = heroDef.abilities[i];
      if (ability && abilityDef) {
        ability.cooldownEndTime = 0;
        ability.charges = abilityDef.charges;
        ability.active = false;
        ability.duration = 0;
      }
    }
    // Keep ultimate charge across rounds (intentional game design)
  }

  setInput(input: PlayerInput): void {
    this.input = { ...input };
  }

  switchToWeaponSlot(slot: number): boolean {
    if (slot < 1 || slot > 10) return false;
    const index = slot - 1;
    
    const targetWeapon = this.weaponInventory[index];
    if (!targetWeapon) return false;
    if (this.currentWeaponSlot === index) return false;
    
    // Don't switch while reloading
    if (this.weapon.reloading) return false;
    
    this.currentWeaponSlot = index;
    this.weapon = targetWeapon;
    return true;
  }

  // Cycle to next owned weapon (mouse wheel down)
  cycleWeaponNext(): boolean {
    if (this.weapon.reloading) return false;
    
    // Find next available weapon slot
    for (let i = 1; i <= 10; i++) {
      const nextSlot = (this.currentWeaponSlot + i) % 10;
      if (this.weaponInventory[nextSlot]) {
        this.currentWeaponSlot = nextSlot;
        this.weapon = this.weaponInventory[nextSlot]!;
        return true;
      }
    }
    return false;
  }

  // Cycle to previous owned weapon (mouse wheel up)
  cycleWeaponPrevious(): boolean {
    if (this.weapon.reloading) return false;
    
    // Find previous available weapon slot
    for (let i = 1; i <= 10; i++) {
      const prevSlot = (this.currentWeaponSlot - i + 10) % 10;
      if (this.weaponInventory[prevSlot]) {
        this.currentWeaponSlot = prevSlot;
        this.weapon = this.weaponInventory[prevSlot]!;
        return true;
      }
    }
    return false;
  }

  getWeaponSlotForId(weaponId: WeaponId): number {
    // Map weapon IDs to slots
    switch (weaponId) {
      case WeaponId.BurstPistol: return 1;
      case WeaponId.NeoSMG9: return 2;
      case WeaponId.PulseRifle: return 3;
      case WeaponId.ScatterShot: return 4;
      case WeaponId.RailSniper: return 5;
      case WeaponId.ShardLauncher: return 6;
      case WeaponId.NanoRepeater: return 7;
      case WeaponId.ArcWelder: return 8;
      case WeaponId.PhaseRepeater: return 9;
      case WeaponId.PlasmaCannon: return 10;
      default: return 1;
    }
  }

  buyWeapon(weaponId: WeaponId, isFree: boolean = false): boolean {
    const weaponDef = WEAPONS[weaponId];
    
    // Check and deduct credits only if not free
    if (!isFree) {
      if (this.credits < weaponDef.price) return false;
      this.credits -= weaponDef.price;
    }
    
    const newWeapon: WeaponState = {
      weaponId,
      ammo: weaponDef.ammoCapacity,
      maxAmmo: weaponDef.ammoCapacity,
      reloading: false,
      reloadEndTime: 0,
      lastFireTime: 0,
    };
    
    // Put in appropriate slot
    const slot = this.getWeaponSlotForId(weaponId);
    const index = slot - 1;
    this.weaponInventory[index] = newWeapon;
    
    // Auto-switch to new weapon
    this.currentWeaponSlot = index;
    this.weapon = newWeapon;

    return true;
  }

  getCollisionBody(): CollisionBody {
    return {
      id: this.id,
      position: this.position,
      velocity: this.velocity,
      collider: {
        type: ColliderType.Circle,
        offset: { x: 0, y: 0 },
        radius: 16,
      },
      layer: this.team === Team.Attackers ? CollisionLayers.PLAYER : CollisionLayers.ENEMY,
      mask: CollisionLayers.WALL | CollisionLayers.PLAYER | CollisionLayers.ENEMY | CollisionLayers.TRIGGER,
      isStatic: false,
      isTrigger: false,
      userData: this,
    };
  }

  toState(): PlayerState {
    return {
      id: this.id,
      name: this.name,
      peerId: this.peerId,
      team: this.team,
      heroId: this.heroId,
      position: { ...this.position },
      velocity: { ...this.velocity },
      rotation: this.rotation,
      health: this.health,
      maxHealth: this.maxHealth,
      shield: this.shield,
      maxShield: this.maxShield,
      alive: this.alive,
      credits: this.credits,
      kills: this.kills,
      deaths: this.deaths,
      assists: this.assists,
      grenadeCount: this.grenadeCount,
      bombCallCount: this.bombCallCount,
      missileStrikeCount: this.missileStrikeCount,
      weapon: { ...this.weapon },
      weaponInventory: this.weaponInventory.map(w => w ? { ...w } : null),
      currentWeaponSlot: this.currentWeaponSlot,
      abilities: this.abilities.map(a => ({ ...a })),
      input: { ...this.input },
    };
  }

  static fromState(state: PlayerState): Player {
    const player = new Player(
      state.peerId,
      state.name,
      state.team,
      state.heroId,
      state.position
    );

    player.velocity = { ...state.velocity };
    player.rotation = state.rotation;
    player.health = state.health;
    player.maxHealth = state.maxHealth;
    player.shield = state.shield;
    player.maxShield = state.maxShield;
    player.alive = state.alive;
    player.credits = state.credits;
    player.kills = state.kills;
    player.deaths = state.deaths;
    player.assists = state.assists;
    player.grenadeCount = state.grenadeCount || 0;
    player.bombCallCount = state.bombCallCount || 0;
    player.missileStrikeCount = state.missileStrikeCount || 0;
    player.weapon = { ...state.weapon };
    player.weaponInventory = state.weaponInventory?.map(w => w ? { ...w } : null) || player.weaponInventory;
    player.currentWeaponSlot = state.currentWeaponSlot ?? player.currentWeaponSlot;
    player.abilities = state.abilities.map(a => ({ ...a }));
    player.input = { ...state.input };

    return player;
  }
}
